import { FunctionEventTypeEnum, JsonPathEnum } from '@libs/types';
import { ConditionActionEnum, JoinConditionActionEnum, parseToPrimitive, TFunctionParsed, TParameter } from './function';
import { PERMISSIONS } from './functionConstants';
import type { TConditions_2, TFunctionParsedAsLink, TFunctionParsedWithoutWidgets } from '../functionViaJsonPath';

type TCondition = {
    text: string;
    joiner: string | null;
};

export const parseConditionStringToObject = (str: string): TFunctionParsedWithoutWidgets | TFunctionParsedAsLink => {
    const result: TFunctionParsedWithoutWidgets = { internalIds: [], conditions: [], isLinkToProperty: false };
    const trimmedStr = str.trim();

    if (!trimmedStr) {
        return { conditions: [], internalIds: [], isLinkToProperty: false };
    }

    // 1. Split the main string by joiners (&&&, |||) but keep them.
    const parts = trimmedStr.split(/(&&&|\|\|\|)/g);

    // 2. Group the condition text with its subsequent joiner.
    const conditions = [] as Array<TCondition>;
    for (let i = 0; i < parts.length; i++) {
        const conditionStr = parts[i].trim();
        if (!conditionStr) continue;

        if (conditionStr === '&&&' || conditionStr === '|||') {
            if (conditions.length > 0) {
                conditions[conditions.length - 1].joiner = conditionStr.slice(0, 2);
            }
        } else {
            conditions.push({ text: conditionStr, joiner: null } as TCondition);
        }
    }

    if (conditions.length === 0) {
        return { conditions: [], internalIds: [], isLinkToProperty: false };
    }

    const jsonPathRegex = /^(\w+)\s*\.(?=\$)(.*)$/;

    // Handle case for a single expression which is just a link to a property
    if (conditions.length === 1 && conditions[0].joiner === null) {
        const conditionText = conditions[0].text;
        const hasOperator = /(==|!=|>=|<=|>|<)/.test(conditionText);
        const jsonPathMatch = conditionText.match(jsonPathRegex);

        if (jsonPathMatch && !hasOperator) {
            const [, id, path] = jsonPathMatch;
            return { isLinkToProperty: true, jsonPath: path.trim(), internalId: id };
        }
    }

    // 3. Parse each grouped condition.
    for (const condition of conditions) {
        // Attempt to match Pattern A: jsonPath operator literal
        const jp_lit_match = condition.text.match(/^(.*)\s*(==|!=|>=|<=|>|<)\s*('[^']*'|\d+(?:\.\d+)?|true|false)$/);
        if (jp_lit_match) {
            const [, lhs, op, literal] = jp_lit_match;
            const trimmedLhs = lhs.trim();
            const jsonPathMatch = trimmedLhs.match(jsonPathRegex);
            if (jsonPathMatch || trimmedLhs.startsWith(PERMISSIONS)) {
                const [, id, path] = jsonPathMatch ?? [];
                const value = literal.startsWith("'") ? literal.slice(1, -1) : literal;
                if (id && !result.internalIds.includes(id)) result.internalIds.push(id);
                result.conditions.push({
                    firstParameter: { value: null, internalId: id, jsonPath: path?.trim() ?? trimmedLhs },
                    secondParameter: { value: parseToPrimitive(value) },
                    conditionAction: op as ConditionActionEnum,
                    joinConditionAction: condition.joiner ? (condition.joiner as JoinConditionActionEnum) : undefined,
                } as TConditions_2);
                continue;
            }
        }

        // Attempt to match Pattern B: literal operator jsonPath
        const lit_jp_match = condition.text.match(/^('[^']*'|\d+(?:\.\d+)?|true|false)\s*(==|!=|>=|<=|>|<)\s*(.*)$/);
        if (lit_jp_match) {
            const [, literal, op, rhs] = lit_jp_match;
            const trimmedRhs = rhs.trim();
            const jsonPathMatch = trimmedRhs.match(jsonPathRegex);
            if (jsonPathMatch || trimmedRhs.startsWith(PERMISSIONS)) {
                const [, id, path] = jsonPathMatch ?? [];
                const value = literal.startsWith("'") ? literal.slice(1, -1) : literal;
                if (id && !result.internalIds.includes(id)) result.internalIds.push(id);
                result.conditions.push({
                    firstParameter: { value: parseToPrimitive(value) },
                    secondParameter: { value: null, internalId: id, jsonPath: path?.trim() ?? trimmedRhs },
                    conditionAction: op as ConditionActionEnum,
                    joinConditionAction: condition.joiner ? (condition.joiner as JoinConditionActionEnum) : undefined,
                } as TConditions_2);
                continue;
            }
        }

        // Attempt to match Pattern C: boolean property check (e.g. widget.$.isValid)
        const jsonPathMatch = condition.text.match(jsonPathRegex);
        if (jsonPathMatch) {
            const [, id, path] = jsonPathMatch;
            if (!result.internalIds.includes(id)) result.internalIds.push(id);
            result.conditions.push({
                firstParameter: { value: null, internalId: id, jsonPath: path.trim() },
                isLinkToProperty: true,
                joinConditionAction: condition.joiner ? (condition.joiner as JoinConditionActionEnum) : undefined,
            } as unknown as TConditions_2);
        }
    }

    if (result.conditions.length === 0 && trimmedStr.length > 0) {
        return { conditions: [], internalIds: [], isLinkToProperty: false };
    }

    return result;
};

const convertToJsonPass = (parameter: TParameter, widgets: TFunctionParsed['widgets']): string | undefined => {
    const propertyName = widgets[parameter.internalId ?? ''] as string;
    return convertEnumToJsonPath(propertyName);
};

export const convertEnumToJsonPath = (propertyName?: string) => {
    switch (propertyName) {
        case FunctionEventTypeEnum.VALUE:
            return JsonPathEnum.VALUE;
        case FunctionEventTypeEnum.ENABLED:
        case FunctionEventTypeEnum.DISABLED:
            return JsonPathEnum.IS_DISABLED;
        case FunctionEventTypeEnum.HIDDEN:
        case FunctionEventTypeEnum.HIDE:
            return JsonPathEnum.IS_HIDE;
        case FunctionEventTypeEnum.IS_VALID:
            return JsonPathEnum.IS_VALID;
        default:
            return undefined;
    }
};

export const convertOldFunctionToNewFunction = (oldFunction: TFunctionParsed, leftPart: string): TFunctionParsedWithoutWidgets => {
    const result: TFunctionParsedWithoutWidgets = {
        internalIds: oldFunction.internalIds,
        conditions: [],
        isOldEnabledFunction: leftPart === FunctionEventTypeEnum.ENABLED,
        isLinkToProperty: false,
    };
    oldFunction.conditions.forEach((oldCondition) => {
        const condition: TConditions_2 = {
            isLinkToProperty: true,
            firstParameter: {
                value: oldCondition.firstParameter.value,
                internalId: oldCondition.firstParameter.internalId,
                jsonPath: convertToJsonPass(oldCondition.firstParameter, oldFunction.widgets),
            },
            conditionAction: oldCondition.conditionAction as ConditionActionEnum,
            joinConditionAction: oldCondition.joinConditionAction,
        };

        if (oldCondition.secondParameter.value !== null || oldCondition.secondParameter.internalId !== null) {
            condition.isLinkToProperty = false;
            condition.secondParameter = {
                value: oldCondition.secondParameter.value,
                internalId: oldCondition.secondParameter.internalId,
                jsonPath: convertToJsonPass(oldCondition.secondParameter, oldFunction.widgets),
            };
        }

        result.conditions.push(condition);
    });

    return result;
};
